import { useCallback, useMemo, useEffect, useState } from 'react';
import classNames from 'classnames';
import OrderProductItem from '../../global/OrderProductItem';
import OrderItemsPreview from './components/OrderItemsPreview';
import { Button, BUTTON_SIZE, BUTTON_TYPE } from '../../global/Button';
import { useMembershipOrderTbbContext, MEMBERSHIP_ORDER_TBB_STEPS } from './MembershipOrderTbbContext';
import { createUserAddress, placeSubscriptionOrder, updateUserAddress } from '../../utils/service';
import AddressInfo from '../../global/AddressInfo';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import MEMBERSHIP_OFFERED_ITEMS, { getFilteredMembershipItems, getSelectionTotalDisplay, validateShipment } from '../../utils/membership';
import { isValidAddress } from '../../utils/addresses';
import EditIconSvg from '../../global/SvgComponents/EditIcon_svg';
import { resetSubscriptionOrderItemsInStorage, SUBSCRIPTION_PAYMENT_TYPES } from '../../utils/subscriptions';
import { RECYCLING_PRODUCT_TYPES } from '../../utils/tbbRegistration';
import { VALIDATE_SHIPPING_TYPES } from '../../config/service';
import InfoButton from '../../global/InfoButton';

import './MembershipOrderTbb.scss';

const MembershipOrderTbb = ({
  title,
  onAddressValidated = () => {},
}) => {
  const { userData } = useFirebaseAuth();

  const {
    selectedAddress,
    navigateToStep,
    membershipAddresses,
    setRefetchMembershipAddresses,
    setSelectedItems,
    setIsLoading,
    setError,
    selectedItems,
    withDefaultValues,
    isFirstOrder,
    isOneTimeOrder,
    isMobileSize,
  } = useMembershipOrderTbbContext();

  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [showEditMode, setShowEditMode] = useState(!isMobileSize);
  const [isValidatingShipping, setIsValidatingShipping] = useState(false);
  const [shippingValidation, setShippingValidation] = useState({
    isValid: true,
    message: null,
    shippingPrice: '0.0',
  });

  const isDashboard = !isFirstOrder && !isOneTimeOrder;

  const filteredSelectedItems = useMemo(() => {
    const filtered = { ...selectedItems };
    delete filtered[
      isOneTimeOrder ? RECYCLING_PRODUCT_TYPES.VIRTUAL : RECYCLING_PRODUCT_TYPES.TECH
    ];
    return filtered;
  }, [selectedItems, isOneTimeOrder]);

  const handleCountChange = useCallback((itemId, newCount) => {
    setSelectedItems(prev => {
      if ([RECYCLING_PRODUCT_TYPES.VIRTUAL, RECYCLING_PRODUCT_TYPES.TECH].includes(itemId)) {
        return {
          ...prev,
          [RECYCLING_PRODUCT_TYPES.VIRTUAL]: newCount,
          [RECYCLING_PRODUCT_TYPES.TECH]: newCount,
        };
      }

      return {
        ...prev,
        [itemId]: newCount,
      };
    });
  }, [setSelectedItems]);

  const handleEditClick = useCallback(() => {
    setShowEditMode(true);
  }, []);

  const totalDisplay = useMemo(() => (
    getSelectionTotalDisplay(filteredSelectedItems, selectedAddress, selectedItems)
  ), [filteredSelectedItems, selectedAddress, selectedItems]);

  const hasItems = useMemo(
    () => Object.values(filteredSelectedItems).some(count => count > 0),
    [filteredSelectedItems],
  );

  const handleAddressAction = () => {
    navigateToStep(MEMBERSHIP_ORDER_TBB_STEPS.ADDRESSES_CONTAINER);
  };

  // eslint-disable-next-line no-unused-vars
  const handleCreateAddress = async (address) => {
    setIsLoading(true);

    if (selectedAddress?.uid) {
      const { error: updateError } = await updateUserAddress({
        addressUid: selectedAddress.uid,
        fromAddress: address,
      });

      if (updateError) {
        setError('Failed to update address');
      }
    } else {
      const { error } = await createUserAddress({
        fromAddress: { ...address, priority: 1 },
      });

      if (error) {
        setError(error.message);
      }
    }

    setRefetchMembershipAddresses(true);

    setIsLoading(false);
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddress || !hasItems) return;

    setIsPlacingOrder(true);

    const products = Object.entries(filteredSelectedItems)
      .filter(([, quantity]) => quantity > 0)
      .map(([productType, quantity]) => ({
        productType: MEMBERSHIP_OFFERED_ITEMS[productType].id,
        quantity,
      }));

    setSelectedItems(filteredSelectedItems);
    const { error } = await placeSubscriptionOrder({
      products,
      userAddressId: selectedAddress.uid,
    });

    setIsPlacingOrder(false);
    if (error) {
      setError(error.message);
    } else {
      resetSubscriptionOrderItemsInStorage();
      navigateToStep(MEMBERSHIP_ORDER_TBB_STEPS.ORDER_CONFIRMATION);
    }
  };

  useEffect(() => {
    if (!selectedAddress || !isValidAddress(selectedAddress)) {
      setShippingValidation({
        isValid: false,
        message: '',
        shippingPrice: '0.0',
      });
      setIsValidatingShipping(false);
      return undefined;
    }

    setIsValidatingShipping(true);
    setShippingValidation({
      isValid: false,
      message: '',
      shippingPrice: '0.0',
    });

    let isCancelled = false;

    const timeoutId = setTimeout(async () => {
      const {
        isValid,
        message,
        shippingPrice,
      } = await validateShipment(
        filteredSelectedItems,
        selectedAddress,
        isOneTimeOrder
          ? VALIDATE_SHIPPING_TYPES.ONE_TIME_ORDER
          : VALIDATE_SHIPPING_TYPES.MEMBERSHIP_ORDER,
      );

      // Only update state if this effect hasn't been cancelled
      if (!isCancelled) {
        setShippingValidation({
          isValid,
          message,
          shippingPrice,
        });
        setIsValidatingShipping(false);
      }
    }, 300);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [selectedAddress, filteredSelectedItems, isOneTimeOrder]);

  useEffect(() => {
    onAddressValidated(shippingValidation.isValid, shippingValidation.shippingPrice);
  }, [shippingValidation, onAddressValidated]);

  const renderEditAddressButton = () => (
    <Button
      type={BUTTON_TYPE.SENARY}
      className="MembershipOrderTbb__delivery-header-button"
      onClick={handleAddressAction}
    >
      <EditIconSvg width={20} height={20} />
    </Button>
  );

  const renderAddressSection = () => (
    <div className="MembershipOrderTbb__delivery-section">
      <div className="MembershipOrderTbb__delivery-header">
        <h3>Delivery Address</h3>
        <InfoButton
          info={'- Trashie Unlimited is for your household use only.\n- Save up to 2 shipping addresses for faster order.'}
        />
        {!isMobileSize && renderEditAddressButton()}
      </div>
      <div className={classNames('MembershipOrderTbb__delivery-address', {
        dashboard: isDashboard,
      })}
      >
        {membershipAddresses.length === 0 || !isValidAddress(selectedAddress) ? (
          <Button
            type={BUTTON_TYPE.QUATERNARY}
            className="MembershipOrderTbb__delivery-address-add"
            onClick={handleAddressAction}
          >
            Add a delivery address
          </Button>
        ) : (
          <>
            {isMobileSize && renderEditAddressButton()}
            <AddressInfo
              firstName={userData?.firstName}
              lastName={userData?.lastName}
              {...selectedAddress}
            />
          </>
        )}
      </div>
      {shippingValidation?.isValid === false && shippingValidation?.message && (
        <div className="MembershipOrderTbb__delivery-address-error">
          {shippingValidation.message}
        </div>
      )}
    </div>
  );

  const renderSummary = () => (
    <div className="MembershipOrderTbb__order-info">
      <span className="MembershipOrderTbb__order-info-value">{totalDisplay}</span>
      <span className="MembershipOrderTbb__order-info-free-shipping">
        Free with Trashie Unlimited
      </span>
    </div>
  );

  const renderItemsSection = () => {
    if (withDefaultValues && !showEditMode) {
      return (
        <div className="MembershipOrderTbb__items-preview-container">
          <OrderItemsPreview
            selectedItems={filteredSelectedItems}
            onEditClick={handleEditClick}
            renderSummary={isMobileSize ? renderSummary : undefined}
          />
        </div>
      );
    }

    return (
      <div className={classNames('MembershipOrderTbb__items-preview-container', {
        dashboard: isDashboard,
      })}
      >
        <div className="MembershipOrderTbb__items">

          {getFilteredMembershipItems(
            isOneTimeOrder
              ? SUBSCRIPTION_PAYMENT_TYPES.ONE_TIME
              : SUBSCRIPTION_PAYMENT_TYPES.SUBSCRIPTION,
          ).map((item) => (
            <OrderProductItem
              key={item.id}
              {...item}
              value={filteredSelectedItems[item.id] || 0}
              onChange={handleCountChange}
              orderDescription={totalDisplay}
              isMembershipOrder
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={classNames('MembershipOrderTbb', {
      'MembershipOrderTbb--placing-order': isPlacingOrder,
    })}
    >
      {title && (
        <div className="MembershipOrderTbb__header">{title}</div>
      )}
      <div className={classNames('MembershipOrderTbb__container', {
        dashboard: isDashboard,
      })}
      >
        {renderItemsSection()}
        <div className="MembershipOrderTbb__container-info">
          {renderAddressSection()}
          {(isDashboard || (!isDashboard && !isMobileSize)) && (
            renderSummary()
          )}
          {isDashboard && (
            <Button
              type={BUTTON_TYPE.QUATERNARY}
              size={BUTTON_SIZE.LARGE}
              onClick={handlePlaceOrder}
              disabled={
                !hasItems || !isValidAddress(selectedAddress) || isPlacingOrder ||
                isValidatingShipping || shippingValidation?.isValid === false
              }
            >
              {isPlacingOrder ? 'CONFIRMING ORDER...' : 'CONFIRM MY ORDER'}
            </Button>
          )}
        </div>
      </div>
      {isPlacingOrder && (
        <div className="MembershipOrderTbb__placing-order-overlay" />
      )}
    </div>
  );
};

export default MembershipOrderTbb;
